import type { AgentWorkspaceModelStatus } from "./agent-workspace-model";

export interface SourceToApplicationCheck {
  id: string;
  status: string;
  observed?: string;
  expected?: string;
}

export interface SourceToApplicationStudioIntegrationContract {
  schemaVersion: "cogo.source-to-application-studio-integration-contract.v1";
  status: string;
  generatedAt?: string;
  artifactId?: string;
  inputs?: Record<string, unknown>;
  policy?: {
    modelMayPatchJsonDirectly?: boolean;
    githubActionsRequired?: boolean;
    directPrivateModelEndpointUse?: boolean;
    studioMustUseProxyModelEndpoints?: boolean;
  };
  studioSurfaces?: string[];
  userFlows?: Array<{
    actor: string;
    steps: string[];
  }>;
  checks?: SourceToApplicationCheck[];
}

export interface SourceToApplicationModelFinding {
  id: string;
  decision?: string;
  severity?: string;
  category?: string;
  observed?: string;
  expected?: string;
  repairIntent?: {
    kind?: string;
    ontologyLayer?: string;
    compiler?: string;
  };
}

export interface SourceToApplicationModelReview {
  schemaVersion: string;
  status: string;
  modelResults?: Array<{
    modelRole: string;
    status: string;
    score?: number;
    latencyMs?: number;
    findings?: SourceToApplicationModelFinding[];
    error?: string;
  }>;
  acceptedFindings?: SourceToApplicationModelFinding[];
  deterministicCandidates?: unknown[];
}

export interface SourceToApplicationRepairSummary {
  schemaVersion: string;
  status: string;
  issues?: Array<{
    id?: string;
    code?: string;
    severity?: string;
    message?: string;
  }>;
  patches?: Array<{
    id: string;
    status: string;
    repairKind?: string;
    ontologyLayer?: string;
    targetPath?: string;
  }>;
  policy?: {
    compiler?: string;
    modelMayPatchJsonDirectly?: boolean;
    allowedRepairKinds?: string[];
  };
}

export interface SourceToApplicationQualityRenderProof {
  schemaVersion: string;
  status: string;
  checks?: SourceToApplicationCheck[];
  screenshots?: {
    beforeRawAmount?: string;
    afterFormattedAmount?: string;
  };
  html?: {
    beforeHtmlPath?: string;
    afterHtmlPath?: string;
  };
}

export interface SourceToApplicationReportSourceGate {
  schemaVersion: string;
  status: string;
  checks?: SourceToApplicationCheck[];
}

export interface ProjectSourceToApplicationQuality {
  schemaVersion: "studio-source-to-application-quality.v1";
  status: string;
  generatedAt?: string;
  integrationContract?: SourceToApplicationStudioIntegrationContract;
  modelReview?: SourceToApplicationModelReview;
  repairSummary?: SourceToApplicationRepairSummary;
  renderProof?: SourceToApplicationQualityRenderProof;
  reportSourceGate?: SourceToApplicationReportSourceGate;
}

export function readRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function readProofField(project: unknown, key: string): unknown {
  const record = readRecord(project);
  if (!record) return null;
  const manifest = readRecord(record.manifest);
  const metadata = readRecord(record.metadata);
  return manifest?.[key] ?? metadata?.[key] ?? record[key] ?? null;
}

function readString(value: unknown, fallback = "unknown"): string {
  return typeof value === "string" && value.length > 0 ? value : fallback;
}

export function readProjectSourceToApplicationQuality(
  project: unknown,
): ProjectSourceToApplicationQuality | null {
  const candidate = readRecord(readProofField(project, "sourceToApplicationQuality"));
  if (!candidate) return null;

  if (candidate.schemaVersion === "studio-source-to-application-quality.v1") {
    return candidate as unknown as ProjectSourceToApplicationQuality;
  }

  if (
    candidate.schemaVersion ===
    "cogo.source-to-application-studio-integration-contract.v1"
  ) {
    return {
      schemaVersion: "studio-source-to-application-quality.v1",
      status: readString(candidate.status, "unknown"),
      generatedAt:
        typeof candidate.generatedAt === "string"
          ? candidate.generatedAt
          : undefined,
      integrationContract:
        candidate as unknown as SourceToApplicationStudioIntegrationContract,
    };
  }

  return null;
}

export function sourceToApplicationModelStatus(
  review: SourceToApplicationModelReview | undefined,
  modelRole: string,
): string {
  return (
    review?.modelResults?.find((result) => result.modelRole === modelRole)
      ?.status ?? "missing"
  );
}

export function sourceToApplicationWorkspaceStatus(
  quality: ProjectSourceToApplicationQuality | null,
  modelRole: "qwen" | "gemma" | "codex-deterministic",
): AgentWorkspaceModelStatus {
  const status = sourceToApplicationModelStatus(
    quality?.modelReview,
    modelRole,
  ).toLowerCase();
  if (status === "missing") return "missing";
  if (status === "pass" || status === "ready" || status === "passed") {
    return "ready";
  }
  if (status === "partial" || status === "warn" || status === "warning") {
    return "warn";
  }
  if (
    status === "fail" ||
    status === "failed" ||
    status === "error" ||
    status === "blocked"
  ) {
    return "blocked";
  }
  return "warn";
}

export function hasReadyFormulaOracleEvidence(project: unknown): boolean {
  const candidates = [
    readProofField(project, "sheetFormulaOracle"),
    readProofField(project, "univerFormulaOracle"),
  ];
  return candidates.some((candidate) => {
    const record = readRecord(candidate);
    const status = readString(record?.status, "").toLowerCase();
    return status === "ready" || status === "pass" || status === "passed";
  });
}

export function hasChartSidecarEvidence(project: unknown): boolean {
  return Boolean(
    readProofField(project, "chartSemanticSidecar") ??
      readProofField(project, "chartSemanticRef") ??
      readProofField(project, "chartSemantic") ??
      readProofField(project, "chartSidecar") ??
      readProofField(project, "sheetCalculationGraph"),
  );
}
